import config from 'services/config'
import { getFile } from 'services/helpers/fetchMethods'
import { fetcher } from 'services/helpers/responseHandler'
import LocalUserService from 'services/localUserService'
import { isRunningInElectron } from 'utils/envHelpers'
import { isRunningAsPWA } from 'utils/pwaHelpers'

const userService = new LocalUserService()

const messagingPaths = {
  sales: 'before',
  service: 'during',
  review: 'after',
  direct: 'direct'
}

const logout = async () => await fetcher.post('login/logout', 'bye!')

const checkLogin = async () => await fetcher.get('login/check')

const updatePaymentStatus = async data =>
  await fetcher.post(`transactionshotel/updatemanually`, data)

const getPayments = async () =>
  await fetcher.get('payments', {
    take: 1000,
    skip: 0
  })

const getAuths = async (count, skip) =>
  await fetcher.get('transactionshotel/invoices', {
    skip,
    take: count
  })

const getBookings = async (count, skip) =>
  await fetcher.get('transactionshotel/bookings', {
    skip,
    take: count
  })

const getUnmaskedCC = async data => await fetcher.post(`purchases/unmaskpan`, data)

const getAuthDetails = async purchaseId =>
  await fetcher.get('purchases/detail', { purchaseId })

const verifyMagicLink = async data =>
  await fetcher.post(`login/confirm-from-link`, data, null, false)

const getAuthLink = async data => await fetcher.post(`InvoiceGenerate`, data)

const shareInvoiceViaSms = async data => await fetcher.post(`Sms`, data)

const shareInvoiceViaEmail = async data => await fetcher.post(`EmailSend`, data)

const getMerchantInfo = async () => await fetcher.get(`merchants/current`)

const updateMerchant = async (merchantId, data) =>
  await fetcher.post(`merchants/${merchantId}`, data)

const setMerchantAsIntegrated = async () =>
  await fetcher.patch(`merchants/integration`, '')

const addMerchantEmail = async (merchantId, model) =>
  await fetcher.put(`merchants/${merchantId}/emails/add`, model)

const updateActiveUserEmail = async email =>
  await fetcher.post(`EmailUpdate`, { BusinessEmailAddress: email })

const getMerchantEmail = async merchantId =>
  await fetcher.get(`merchants/${merchantId}/emails`)

const removeMerchantEmail = async (merchantId, model) =>
  await fetcher.del(`merchants/${merchantId}/emails/remove`, model)

const getAuditData = async () => await fetcher.get(`creditcard/unmaskHistory/users`)

const getAuditDataForUser = async (accountId, startDate, endDate, take, skip) =>
  await fetcher.get('creditcard/unmaskHistory/user', {
    accountId,
    startDate,
    endDate,
    take,
    skip
  })

const getUnmaskHistoryForTransaction = async (purchaseId, paymentGatewayReference) =>
  await fetcher.get('creditcard/unmaskHistory/purchase', {
    purchaseId,
    paymentReference: paymentGatewayReference
  })

const autoinviteSnapshot = async model =>
  await fetcher.post(`inviteanalytics/snapshot`, model)

// uploadAutoinviteGuestDataUrl = `invite/upload`

// apiUrl = `${config.apiUrl}`

const getReverseGeolocation = async (latitude, longitude) =>
  fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?address=${latitude},${longitude}&key=${config.gmapsKey}`
  )
    .then(res => res.json())
    .then(data => data)
    .catch(error => `something went wrong while getting reverse geolocation: ${error}`)

const postSelectedHotelMerchant = async subsidiaryId =>
  await fetcher.put(`usersubsidiaries/switch`, { subsidiaryId })

const getHotelListForMerchant = async () => await fetcher.get(`usersubsidiaries/`)

const postRefund = async (purchaseId, paymentGatewayReference, refundAmount) =>
  await fetcher.post(`payments/refundnew`, {
    purchaseId,
    paymentGatewayReference,
    refundAmount
  })

// login

const loginBegin = async ({ email, phone, geoLocation, screen }) => {
  const isPWA = isRunningAsPWA()
  const isRunningAsElectron = isRunningInElectron()

  return await fetcher.post(
    `login/begin${isPWA || isRunningAsElectron ? '?lang=en' : ''}`,
    {
      email,
      phone,
      geoLocation,
      screen: isRunningAsElectron ? `electronResolution` : screen
    },
    null,
    false
  )
}

const renewSession = async ({
  sessionId,
  userToken,
  geoLocation,
  screen,
  subsidiaryId
}) => {
  const isPWA = isRunningAsPWA()
  const isRunningAsElectron = isRunningInElectron()

  // Build payload
  const payload = {
    sessionId,
    userToken,
    geoLocation,
    screen: isRunningAsElectron ? `electronResolution` : screen,
    subsidiaryId
  }

  const shouldHaveLongSession = isRunningAsElectron || isPWA

  return await fetcher.post(
    `login/authorize${shouldHaveLongSession ? '?lang=en' : ''}`,
    payload,
    null,
    false
  )
}

const generateQr = async () => {
  const isPWA = isRunningAsPWA()

  return await fetcher.get('login/generateqr', isPWA ? null : { lang: 'en' }, null, false)
}

// media

const getMediaList = async (category, lastPositionToken) =>
  await fetcher.post(`media/list`, { category, lastPositionToken })

const getMedia = async (category, name) => {
  await fetcher.get(`api-media/${category}/${name}`)
}

// upload logs

const getUploadLogs = async () => await fetcher.get(`invitehistory/load`)

const downloadUploadLog = async (historyId, uploadHistoryKind, category = 'Denied') =>
  await getFile(
    `invitehistory/download?requestFor=${category}&historyId=${historyId}&uploadHistoryKind=${uploadHistoryKind}`
  )

const getSingleUserLog = async subscriberId =>
  await fetcher.get(`invitesubscribers/load/subscriber?subscriberId=${subscriberId}`)

const getSingleUserBookings = async subscriberId =>
  await fetcher.get(
    `invitesubscribers/load/subscriber/bookings?subscriberId=${subscriberId}`
  )

const getPriorBookings = async bookingId =>
  await fetcher.post(`bookings/priors`, { bookingId })

// Reviews

const getReviewsRequests = async (fromDate, toDate) =>
  await fetcher.get(`invitereview/snapshot?fromDate=${fromDate}&toDate=${toDate}`)

const getDailyReviews = async requestDate =>
  await fetcher.post(`invitereview/dayresponses`, { requestDate })

// add-edit new hotel

const addHotelGroup = async group => await fetcher.post('merchants/add/parent', group)
const addHotel = async hotel => await fetcher.post('merchants/add/subsidiary', hotel)

const checkShortLinkAvailability = async shortLink =>
  await fetcher.post(`merchants/validate/shortink`, shortLink)

const updateHotelLogo = async logo =>
  await fetcher.post(`merchants/update/subsidiary/logo`, logo)

const searchNumbers = async (input, hotelId) =>
  await fetcher.post(`merchants/subsidiary/searchphonenumbers?id=${hotelId}`, input)

const initializeBandwidth = async subsidiaryId =>
  await fetcher.post(`telcosetup`, {
    subsidiaryId,
    InitializeBandwidth: true
  })

const checkPhoneNumber = async number => {
  try {
    const res = await fetch(
      `${config.apiUrl}/general/validate/phone/number?phoneNumber=${number}`,
      {
        headers: {
          'sp-auth': userService.getCurrentUserInfo()?.sessionId
        }
      }
    )

    if (res.status === 200) return await res.text()

    return false
  } catch (error) {
    return false
  }
}

const checkPhoneNumberWithPrefix = async number =>
  await fetcher.get(
    'general/validate/phone/number',
    {
      phoneNumberWithPrefix: number
    },
    null,
    true,
    'text'
  )

const sendPrimaryNumber = async (reservationProxyLine, hotelId) =>
  await fetcher.post(`merchants/update/subsidiary/byInternal?subsidiaryId=${hotelId}`, {
    reservationProxyLine
  })

// invoices & billing

const getInvoice = async invoiceIds =>
  await fetcher.post(`billing/invoices`, { invoiceIds })

const getInvoiceList = async (isForGroup = false, subsidiaryIdRequested) =>
  await fetcher.post(`billing/load/subsidiary/forbilling`, {
    isForGroup,
    subsidiaryIdRequested
  })

const getPaymentsList = async (isForGroup = false) =>
  await fetcher.get(`billing/payment/retrieve?isForGroup=${isForGroup}`)

const getBillingContacts = async () =>
  await fetcher.get(`users/forbilling?isForGroup=false`)

const getSavedPaymentMethods = async () =>
  await fetcher.get(`billing/payment/accounts?isForGroup=false`)

const addPayment = async model =>
  await fetcher.post(`billing/payment/add/credit-card`, model)

const addPaymentAccount = async model =>
  await fetcher.post(`billing/payment/account/add`, model)

const makePaymentMethodDefaultNew = async defaultAccountId =>
  fetcher.put('billing/payment/account/default', {
    defaultAccountId,
    isForGroup: false
  })

const sendInvoice = async model => await fetcher.post(`billing/invoice/send`, model)

const invoiceReviewComplete = async invoiceId =>
  await fetcher.post(`billing/invoice/update`, { invoiceId })

const getBillableBookings = async (invoiceId, subsidiaryId) =>
  await fetcher.post(`merchants/load/subsidiary/invoice/forreview`, {
    invoiceId,
    subsidiaryId
  })

const postChangedConversation = async model =>
  await fetcher.post(`merchants/update/subsidiary/invoice/conversions`, model)

const markInvoiceAsPaid = async (invoiceId, paymentInfo) =>
  await fetcher.post(`merchants/subsidiary/invoice/close`, {
    invoiceId,
    ...paymentInfo
  })

const retryPayment = async id => await fetcher.put('billing/payment/retry', id)

const updatePayment = async model => await fetcher.post('billing/payment/status', model)

// blacklist

const postAnalyze = async model => await fetcher.post(`billing/exclusion/analyze`, model)

const postBlacklist = async model => await fetcher.post(`billing/exclusion/add`, model)

const removeExclusion = async id => await fetcher.post(`billing/exclusion/remove`, id)

const addDirectBlacklist = async model =>
  await fetcher.post(`billing/exclusion/adddirect`, model)

// hotels and groups

const getHotelsAndGroups = async (includingDeleted = false, take = 50, skip = 0) =>
  await fetcher.get(
    `merchants/load?includingDeleted=${includingDeleted}&skip=${skip}&take=${take}`
  )

const getSubsidiaryDetails = async id =>
  await fetcher.post(`merchants/subsidiary/load/config`, `'${id}'`)

// initiate messaging

const getArrivals = async () => await fetcher.get(`transactionshotel/arrivals`)

const postNewGuestForChat = async model => await fetcher.post(`chat/service/start`, model)

// messaging

const getDailyConversations = async date =>
  await fetcher.post(`frontdesk/conversations`, date)

const getChats = async kind => await fetcher.post(`chat/list/${messagingPaths[kind]}`, {}) // before, during, after

const getThread = async (chatId, kind) =>
  await fetcher.post(`chat/retrieve/${messagingPaths[kind]}`, { chatId }) // before, during, after

const sendMessage = async ({
  chatId,
  message,
  controlKey,
  mediaUrls = [],
  subject,
  messageKind,
  email
}) =>
  await fetcher.post(`chat/send`, {
    chatId,
    message,
    controlKey,
    mediaUrls,
    subject,
    messageKind,
    email
  })

const resolveThread = async chatId => await fetcher.post(`chat/close`, chatId)

const postTyping = async ({ chatId, isTyping }) =>
  await fetcher.post(`chat/typing`, { chatId, isTyping })

const oneTimerAnnualGuests = async () =>
  await fetcher.get('inviteanalytics/onetime/annual')

const repeatAnnualGuests = async () => await fetcher.get('inviteanalytics/repeat/annual')

const guestInsights = async () => await fetcher.get('inviteanalytics/insights/annual')

const reservationLengthInsights = async () =>
  await fetcher.get(`inviteanalytics/insights/nights/annual`)

const postTakeOwnership = async chatId => await fetcher.post('chat/ownershipTake', chatId)

const updateChatThread = async model => await fetcher.patch('subscribers', model)

const getChatMedia = async (chatId, messageId, kind) =>
  await fetcher.post(`chat/retrieve/${messagingPaths[kind]}/media`, {
    chatId,
    messageId,
    kind
  })

// intents

const getResolvedChats = async data => await fetcher.post(`chat/resolved`, data)

const getChatIntentTypes = async () => await fetcher.get(`frontdesk/categories`)

const putChatIntents = async data => await fetcher.put(`chat`, data)

// services

const modifyService = async (model, id) =>
  await fetcher.post(`billing/services/modify?subsidiaryId=${id}`, [model])

// push

const sendPushSubscription = async model => await fetcher.post(`users/push/update`, model)

// log

const log = async model => await fetcher.post('logs', model)

// health check

const getHealthCheckInvite = async (periodStart, periodEnd) =>
  await fetcher.post('internalonly/checkups/invitetelco', { periodStart, periodEnd })

const getHealthCheckSync = async () => await fetcher.post('internalonly/checkups/sync')

const getHealthCheckBilling = async () =>
  await fetcher.post('internalonly/checkups/billing')

const getHealthCheckInvoices = async () =>
  await fetcher.post('internalonly/checkups/performance')

// mobile app

const getMobileAppQR = async () => await fetcher.get('mobileapp/qr')

const getEmailLogs = async model => await fetcher.post('internalonly/emails/sent', model)

// users

const getUsers = async merchantId =>
  await fetcher.get(`users${merchantId ? `?merchantId=${merchantId}` : ''}`)

// custom campaigns

const getRateCodesByTracking = async trackingCode =>
  await fetcher.post(`audience/discover`, { trackingCode })

// tools

const serverSideFetch = async (host, url) =>
  await fetcher.post(
    'internalonly/url/get',
    {
      host,
      fullUri: url
    },
    null,
    true,
    'text'
  )

const dashboardApi = {
  logout,
  checkLogin,
  updatePaymentStatus,
  getPayments,
  getAuths,
  getBookings,
  getUnmaskedCC,
  getAuthDetails,
  verifyMagicLink,
  getAuthLink,
  shareInvoiceViaSms,
  shareInvoiceViaEmail,
  getMerchantInfo,
  updateMerchant,
  setMerchantAsIntegrated,
  addMerchantEmail,
  updateActiveUserEmail,
  getMerchantEmail,
  removeMerchantEmail,
  getAuditData,
  getAuditDataForUser,
  getUnmaskHistoryForTransaction,
  autoinviteSnapshot,
  getReverseGeolocation,
  postSelectedHotelMerchant,
  getHotelListForMerchant,
  postRefund,
  loginBegin,
  renewSession,
  generateQr,
  getMediaList,
  getMedia,
  getUploadLogs,
  downloadUploadLog,
  getSingleUserLog,
  getSingleUserBookings,
  getPriorBookings,
  getReviewsRequests,
  getDailyReviews,
  addHotelGroup,
  addHotel,
  checkShortLinkAvailability,
  updateHotelLogo,
  searchNumbers,
  initializeBandwidth,
  checkPhoneNumber,
  checkPhoneNumberWithPrefix,
  sendPrimaryNumber,
  getInvoice,
  getInvoiceList,
  getPaymentsList,
  getBillingContacts,
  getSavedPaymentMethods,
  addPayment,
  addPaymentAccount,
  makePaymentMethodDefaultNew,
  sendInvoice,
  invoiceReviewComplete,
  getBillableBookings,
  postChangedConversation,
  markInvoiceAsPaid,
  retryPayment,
  updatePayment,
  postAnalyze,
  postBlacklist,
  removeExclusion,
  addDirectBlacklist,
  getHotelsAndGroups,
  getSubsidiaryDetails,
  getArrivals,
  postNewGuestForChat,
  getDailyConversations,
  getChats,
  getThread,
  sendMessage,
  resolveThread,
  postTyping,
  oneTimerAnnualGuests,
  repeatAnnualGuests,
  guestInsights,
  reservationLengthInsights,
  postTakeOwnership,
  updateChatThread,
  getChatMedia,
  getResolvedChats,
  getChatIntentTypes,
  putChatIntents,
  modifyService,
  sendPushSubscription,
  log,
  getHealthCheckInvite,
  getHealthCheckSync,
  getHealthCheckBilling,
  getHealthCheckInvoices,
  getMobileAppQR,
  getEmailLogs,
  getUsers,
  getRateCodesByTracking,
  serverSideFetch
}

export default dashboardApi
